
import { useState, FormEvent } from 'react';

const tipCategories = [
  'City Council',
  'Schools',
  'Development',
  'Public Safety',
  'Business',
  'Community',
  'Sports',
  'Other',
];

export default function SubmitNewsTip() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      return;
    }

    setIsSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d6bpolitehdqnvnpi3sg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          // FormData entries are not directly iterable as Record<string,string>,
          // so we convert them safely.
          Array.from(formData.entries()).reduce<Record<string, string>>(
            (acc, [key, value]) => {
              acc[key] = String(value);
              return acc;
            },
            {}
          )
        ).toString(),
      });
      setIsSuccess(true);
      form.reset();
      setTimeout(() => {
        setIsSuccess(false);
        setIsOpen(false);
      }, 3000);
    } catch (error) {
      // Log the error for debugging while keeping UI silent for users
      console.error('Failed to submit tip:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap shadow-md"
      >
        <i className="ri-quill-pen-line text-lg"></i>
        Submit a News Tip
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                  <i className="ri-quill-pen-line text-xl text-amber-600"></i>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Submit a News Tip</h2>
                  <p className="text-xs text-gray-500">Help us cover what matters to Roseville</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-400"></i>
              </button>
            </div>

            {/* Form or Success Message */}
            {isSuccess ? (
              <div className="p-10 text-center">
                <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Tip Submitted!</h3>
                <p className="text-gray-500 text-sm">
                  Thank you for your contribution. Our editorial team will review your tip shortly.
                </p>
              </div>
            ) : (
              <form
                id="news-tip-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="p-6 space-y-5"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Jane Doe"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="jane@example.com"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Phone <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="(916) 555-0123"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer appearance-none bg-white"
                  >
                    <option value="">Select a category</option>
                    {tipCategories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Headline / Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="headline"
                    required
                    placeholder="Brief headline for your tip"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Details <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="details"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Describe the story, include who, what, when, where, and why if possible..."
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      const counter = target.parentElement?.querySelector('.char-count');
                      if (counter) {
                        counter.textContent = `${target.value.length}/500`;
                      }
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">
                    <span className="char-count">0/500</span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Location <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    placeholder="Where did this happen? e.g., Downtown Roseville"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    name="anonymous"
                    id="anonymous"
                    value="Yes"
                    className="mt-1 cursor-pointer"
                  />
                  <label htmlFor="anonymous" className="text-sm text-gray-600 cursor-pointer">
                    I wish to remain anonymous (your contact info will not be published)
                  </label>
                </div>

                <div className="pt-2 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="inline-flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Tip'
                    )}
                  </button>
                </div>

                <p className="text-[11px] text-gray-400 text-center leading-relaxed">
                  By submitting, you agree that our editorial team may follow up for additional
                  details. All tips are reviewed and verified before publication.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
